import { createContext, useCallback, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useMachine, useSelector } from '@xstate/react';

import { workflowMachine, type WorkflowData } from '.';
import type { AuthStatus, Steps } from './schemaTypes';
import { useQuery } from '@tanstack/react-query';
import { QUERIES } from '../utils/constants';
import { getRetailerSettingsQueryFn } from '../graphql/query/getRetailerSettings';
import { isValidWorkflowRoute } from '../utils/workflow';
import type { Order, SubmittedRequest } from '../graphql/query/getOrder';
import type { MonetaryValue } from '../graphql/query/getRefundOptions';
import type { TaskRuntimeContext } from './types';

type WorkflowContextType = ReturnType<typeof useWorkflowState> | null;
export const WorkflowContext = createContext<WorkflowContextType>(null);

export const useWorkflowState = () => {
  const [snapshot, send, actorRef] = useMachine(workflowMachine);

  const { context } = snapshot;
  const {
    authStatus,
    config,
    data,
    errors,
    retailerSettings,
    isSaving,
    isLoading,
    loginContext,
    order,
    taskRuntimeContext,
    orderNumber,
    requestId,
    retailer,
    shid,
    submittedRequests,
    currentStepIndex,
    error,
    orderError,
    redirectTo,
    refundAmount,
  } = context;

  const isAuthenticated = authStatus === 'authenticated';

  // Just preloading for cache consumption in the language component
  useQuery({
    retry: false,
    queryKey: [QUERIES.GET_RETAILER_SETTINGS],
    queryFn: () => getRetailerSettingsQueryFn(),
  });

  const navigate = useNavigate();
  const location = useLocation();

  const currentStep = useSelector(
    actorRef,
    useCallback((snapshot) => {
      const { context } = snapshot;
      const { config, currentStepIndex } = context;

      // If the machine is in a final state, don't return a current step
      if (snapshot.status === 'done') {
        return null;
      }

      if (!config) return null;

      return config.steps[currentStepIndex];
    }, []),
  );

  const canMoveToPrev = useSelector(actorRef, (snapshot) => {
    const { context } = snapshot;
    const { currentStepIndex } = context;

    return currentStepIndex > 0;
  });

  const isFirstStep = useSelector(actorRef, (snapshot) => {
    const { context } = snapshot;
    const { config, currentStepIndex } = context;

    if (!config) return false;

    const currentStep = config.steps[currentStepIndex];

    if (!currentStep) return false;

    return currentStepIndex === 0;
  });

  const isLastStep = useSelector(actorRef, (snapshot) => {
    const { context } = snapshot;
    const { config, currentStepIndex } = context;

    if (!config) return false;

    const currentStep = config.steps[currentStepIndex];

    if (!currentStep) return false;

    return currentStepIndex === config.steps.length - 1;
  });

  const isSettled = useSelector(
    actorRef,
    useCallback((snapshot) => {
      const value = snapshot.value;
      if (typeof value === 'object' && value !== null) {
        return 'ready' in value;
      }
      return value === 'notAuthenticated' || value === 'error';
    }, []),
  );

  const redirectToRetailerPath = useCallback(
    (path: string) => {
      navigate({
        to: path,
        params: { retailer: retailer ?? 's' },
        search: (prev) => {
          const newSearch = { ...prev };
          delete newSearch.retailer;
          return newSearch;
        },
      });
    },
    [navigate, retailer],
  );

  useEffect(() => {
    // We first handle the claim details case because it's a special case
    // and we need to redirect to the correct route
    if (location.pathname === '/claim-details') {
      redirectToRetailerPath('/$retailer/claim-details');
      return;
    }

    // Wait for the machine to reach a stable state before making auth-dependent
    // redirects. During loading (loadConfig, loadOrder, etc.), authStatus may
    // reflect a stale value from session storage, causing premature redirects
    // that result in a redirect loop when the actual auth check completes.
    if (!isSettled) return;

    const isHomeOrWorkflowRoute =
      location.pathname === '/' ||
      location.pathname === `/${retailer}` ||
      location.pathname === `/${retailer}/` ||
      isValidWorkflowRoute(location.pathname);

    // Check if this is an orderless route
    const isOrderlessRoute =
      location.pathname.endsWith('/orderless') ||
      location.pathname === `/${retailer}/orderless`;

    // Don't redirect from completion pages or other special routes
    const isCompletionRoute =
      location.pathname === `/${retailer}/complete` ||
      location.pathname.endsWith('/complete');

    // For orderless routes, allow access if isOrderless is enabled (no auth required)
    // For other routes, require authentication
    // Also allow access on home routes for orderless retailers
    const shouldAllowAccess =
      isAuthenticated ||
      (isOrderlessRoute && retailerSettings?.features?.isOrderless) ||
      (isHomeOrWorkflowRoute && retailerSettings?.features?.isOrderless);

    // If the current path is a home or workflow route and the user has access,
    // we navigate to the current step (but don't redirect from completion pages)
    if (
      isHomeOrWorkflowRoute &&
      currentStep &&
      shouldAllowAccess &&
      !isCompletionRoute
    ) {
      redirectToRetailerPath(currentStep.route);
    }
  }, [
    isSettled,
    currentStep,
    location.pathname,
    retailer,
    isAuthenticated,
    retailerSettings?.features?.isOrderless,
    config,
    redirectToRetailerPath,
  ]);

  // Handle redirect when redirectTo is set by the state machine
  useEffect(() => {
    if (redirectTo) {
      navigate(redirectTo);
    }
  }, [redirectTo, navigate]);

  return {
    loginContext,
    order,
    taskRuntimeContext,
    orderNumber,
    requestId,
    retailer,
    shid,
    config,
    retailerSettings,
    features: retailerSettings?.features,
    currentStep,
    currentStepIndex,
    canMoveToPrev,
    data,
    errors,
    refundAmount,
    complete: () => send({ type: 'complete' }),
    goTo: (stepId: Steps) => send({ type: 'goTo', stepId }),
    isAuthenticated,
    isFirstStep,
    isLastStep,
    isSaving,
    isLoading,
    next: (data: WorkflowData) => {
      send({ type: 'next', data });
    },
    prev: () => send({ type: 'prev' }),
    logout: () => {
      send({ type: 'logout' });
    },
    setRefundAmount: useCallback(
      (refundAmount: MonetaryValue | null) => {
        send({ type: 'setRefundAmount', refundAmount });
      },
      [send],
    ),
    setContextFromLogin: useCallback(
      ({
        loginContext,
        order,
        submittedRequests,
        taskRuntimeContext,
      }: {
        loginContext: {
          email?: string;
          orderNumber?: string;
          zipCode?: string;
        };
        order: Order;
        submittedRequests: SubmittedRequest[];
        taskRuntimeContext?: Partial<TaskRuntimeContext>;
      }) => {
        send({
          type: 'setContextFromLogin',
          data: {
            loginContext,
            order,
            submittedRequests,
            taskRuntimeContext,
          },
        });
      },
      [send],
    ),
    submittedRequests,
    updateAuthStatus: useCallback(
      (status: AuthStatus) => {
        send({ type: 'updateAuthStatus', status });
      },
      [send],
    ),
    updateData: (data: WorkflowData) => {
      send({ type: 'updateData', data });
    },
    updateIsSaving: useCallback(
      (isSaving: boolean) => {
        send({ type: 'updateIsSaving', isSaving });
      },
      [send],
    ),
    currentState: snapshot.value,
    error,
    orderError,
  };
};

export const useWorkflow = () => {
  const context = useContext(WorkflowContext);
  if (!context) {
    throw new Error('useWorkflow must be used within a WorkflowProvider');
  }
  return context;
};

export const useSafeWorkflow = () => {
  return useContext(WorkflowContext) || undefined;
};
